/**
 * Canonical Mastery Module — Single Source of Truth
 *
 * Family-first mastery system with group-scoped progress.
 * This is the ONLY module for mastery calculations.
 *
 * MeesterJay 4.1 Architecture:
 * - FamilyMastery is the authoritative mastery source
 * - Module/group completion derived from family mastery
 * - Question states are attempt history only (NOT mastery)
 * - All progress is group-scoped
 */

import {
  type ModuleId,
  getFamiliesForModule,
  getFamilyDefinition,
  type FamilyDefinition,
  getAllowedTiersForGroup,
} from '../content'
import { FamilyMasteryState } from './familyMasteryEngine'
import { GroupId } from '../config/systemConfig'
import { getFamilyMasteryKey } from './familyMasteryEngine'
import { MASTERY_LEVEL_THRESHOLDS } from '../config/constants'

// ============================================================
// FAMILY MASTERY QUERIES (Canonical - Group-Scoped)
// ============================================================

/**
 * Get the mastery score for a specific familyId in a specific group.
 * Returns 0 if the family has never been attempted in this group.
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param familyId - The canonical familyId
 * @param group - The school group (6, 7, or 8)
 * @returns Mastery score (0-100)
 */
export function getFamilyMasteryScore(
  familyMastery: Record<string, FamilyMasteryState>,
  familyId: string,
  group: GroupId,
): number {
  const key = getFamilyMasteryKey(familyId, group)
  const state = familyMastery[key]
  return state?.masteryScore ?? 0
}

/**
 * Check if a family is considered "mastered" (score >= 85) in a specific group.
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param familyId - The canonical familyId
 * @param group - The school group (6, 7, or 8)
 * @returns True if mastered in this group
 */
export function isFamilyMastered(
  familyMastery: Record<string, FamilyMasteryState>,
  familyId: string,
  group: GroupId,
): boolean {
  return getFamilyMasteryScore(familyMastery, familyId, group) >= 85
}

/**
 * Get a human-readable mastery level label for a score.
 */
export function getFamilyMasteryLevel(
  score: number,
): 'onbekend' | 'beginner' | 'oefenaar' | 'gevorderd' | 'meester' {
  if (score <= 0) return 'onbekend'
  if (score < MASTERY_LEVEL_THRESHOLDS.BEGINNER) return 'beginner'
  if (score < MASTERY_LEVEL_THRESHOLDS.OEFENAAR) return 'oefenaar'
  if (score < MASTERY_LEVEL_THRESHOLDS.GEVORDERD) return 'gevorderd'
  return 'meester'
}

/**
 * Alias for getFamilyMasteryLevel — backward compatibility.
 * Consolidated from familyMasteryEngine.ts (D-SCAN D-2).
 */
export const getMasteryLevel = getFamilyMasteryLevel

/**
 * Get color class for mastery score (for UI display).
 */
export function getMasteryColor(score: number): string {
  if (score < MASTERY_LEVEL_THRESHOLDS.BEGINNER) return 'text-rose-600'
  if (score < MASTERY_LEVEL_THRESHOLDS.OEFENAAR) return 'text-amber-600'
  if (score < MASTERY_LEVEL_THRESHOLDS.GEVORDERD) return 'text-emerald-600'
  return 'text-blue-600'
}

/**
 * Get background color class for mastery score.
 */
export function getMasteryBgColor(score: number): string {
  if (score < MASTERY_LEVEL_THRESHOLDS.BEGINNER) return 'bg-rose-100'
  if (score < MASTERY_LEVEL_THRESHOLDS.OEFENAAR) return 'bg-amber-100'
  if (score < MASTERY_LEVEL_THRESHOLDS.GEVORDERD) return 'bg-emerald-100'
  return 'bg-blue-100'
}

// ============================================================
// MODULE MASTERY (Canonical - Group-Scoped)
// ============================================================

/**
 * Get module mastery from family mastery (CANONICAL).
 * This is the authoritative way to compute module mastery.
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param moduleId - Module to query
 * @param group - School group (REQUIRED for group-scoped mastery)
 * @returns Mastery percentage (0-100)
 */
export function getModuleMasteryFromFamilies(
  familyMastery: Record<string, FamilyMasteryState>,
  moduleId: ModuleId,
  group: GroupId,
): number {
  let moduleFamilies = getFamiliesForModule(moduleId)

  // Filter by group tier range
  const allowedTiers = getAllowedTiersForGroup(group)
  moduleFamilies = moduleFamilies.filter((f) => allowedTiers.has(f.tier))
  if (moduleFamilies.length === 0) return 0

  // Only count families that have been attempted IN THIS GROUP
  const attempted = moduleFamilies
    .map((def) => {
      const key = getFamilyMasteryKey(def.familyId, group)
      return familyMastery[key]
    })
    .filter(
      (state): state is FamilyMasteryState =>
        state !== undefined && state.attempts > 0,
    )
  if (attempted.length === 0) return 0

  // Average mastery across attempted families
  const totalScore = attempted.reduce((sum, s) => sum + s.masteryScore, 0)
  return Math.round(totalScore / attempted.length)
}

/**
 * Get module completion status from family mastery (CANONICAL).
 * A module is "mastered" when a threshold percentage of families are mastered.
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param moduleId - Module to query
 * @param group - School group (REQUIRED)
 * @param threshold - Percentage of families that must be mastered (default: 0.85 = 85%)
 * @returns True if module is mastered in this group
 */
export function isModuleMasteredByFamilies(
  familyMastery: Record<string, FamilyMasteryState>,
  moduleId: ModuleId,
  group: GroupId,
  threshold: number = 0.85,
): boolean {
  let moduleFamilies = getFamiliesForModule(moduleId)

  // Filter by group tier range
  const allowedTiers = getAllowedTiersForGroup(group)
  moduleFamilies = moduleFamilies.filter((f) => allowedTiers.has(f.tier))
  if (moduleFamilies.length === 0) return false

  // Count mastered families (mastery >= 85) IN THIS GROUP
  const masteredCount = moduleFamilies.filter((def) => {
    const key = getFamilyMasteryKey(def.familyId, group)
    const state = familyMastery[key]
    return state && state.masteryScore >= 85
  }).length

  // Check if threshold percentage is met
  return masteredCount / moduleFamilies.length >= threshold
}

// ============================================================
// TIER-SPECIFIC MASTERY (Group-Scoped)
// ============================================================

/**
 * Get tier-specific mastery from family mastery.
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param moduleId - Module to query
 * @param tier - Tier to query (1-16)
 * @param group - School group (REQUIRED)
 * @returns Mastery percentage for this tier (0-100)
 */
export function getTierMasteryFromFamilies(
  familyMastery: Record<string, FamilyMasteryState>,
  moduleId: ModuleId,
  tier: number,
  group: GroupId,
): number {
  let moduleFamilies = getFamiliesForModule(moduleId)

  // Filter by tier
  moduleFamilies = moduleFamilies.filter((f) => f.tier === tier)

  // Filter by group tier range
  const allowedTiers = getAllowedTiersForGroup(group)
  moduleFamilies = moduleFamilies.filter((f) => allowedTiers.has(f.tier))
  if (moduleFamilies.length === 0) return 0

  // Only count families that have been attempted IN THIS GROUP
  const attempted = moduleFamilies
    .map((def) => {
      const key = getFamilyMasteryKey(def.familyId, group)
      return familyMastery[key]
    })
    .filter(
      (state): state is FamilyMasteryState =>
        state !== undefined && state.attempts > 0,
    )
  if (attempted.length === 0) return 0

  // Average mastery across attempted families in this tier
  const totalScore = attempted.reduce((sum, s) => sum + s.masteryScore, 0)
  return Math.round(totalScore / attempted.length)
}

/**
 * Get mastery by tier for all tiers in a module.
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param moduleId - Module to query
 * @param group - School group (REQUIRED)
 * @returns Record of tier → mastery percentage
 */
export function getAllTierMasteryFromFamilies(
  familyMastery: Record<string, FamilyMasteryState>,
  moduleId: ModuleId,
  group: GroupId,
): Record<number, number> {
  let moduleFamilies = getFamiliesForModule(moduleId)

  // Filter by group tier range
  const allowedTiers = getAllowedTiersForGroup(group)
  moduleFamilies = moduleFamilies.filter((f) => allowedTiers.has(f.tier))

  // Get unique tiers
  const tiers = [...new Set(moduleFamilies.map((f) => f.tier))]

  // Compute mastery for each tier
  const result: Record<number, number> = {}
  tiers.forEach((tier) => {
    result[tier] = getTierMasteryFromFamilies(
      familyMastery,
      moduleId,
      tier,
      group,
    )
  })
  return result
}

// ============================================================
// FAMILY QUERIES (Group-Scoped)
// ============================================================

/**
 * Get struggling families for a module (mastery < 40).
 * Useful for recommendations and focus areas.
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param moduleId - Module to query
 * @param group - School group (REQUIRED)
 * @returns Array of struggling family states, sorted by mastery (lowest first)
 */
export function getStrugglingFamiliesForModule(
  familyMastery: Record<string, FamilyMasteryState>,
  moduleId: ModuleId,
  group: GroupId,
): FamilyMasteryState[] {
  let moduleFamilies = getFamiliesForModule(moduleId)

  // Filter by group tier range
  const allowedTiers = getAllowedTiersForGroup(group)
  moduleFamilies = moduleFamilies.filter((f) => allowedTiers.has(f.tier))
  return moduleFamilies
    .map((def) => {
      const key = getFamilyMasteryKey(def.familyId, group)
      return familyMastery[key]
    })
    .filter(
      (state): state is FamilyMasteryState =>
        state !== undefined && state.attempts > 0 && state.masteryScore < 40,
    )
    .sort((a, b) => a.masteryScore - b.masteryScore)
}

/**
 * Get mastered families for a module (mastery >= 85).
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param moduleId - Module to query
 * @param group - School group (REQUIRED)
 * @returns Array of mastered family states
 */
export function getMasteredFamiliesForModule(
  familyMastery: Record<string, FamilyMasteryState>,
  moduleId: ModuleId,
  group: GroupId,
): FamilyMasteryState[] {
  let moduleFamilies = getFamiliesForModule(moduleId)

  // Filter by group tier range
  const allowedTiers = getAllowedTiersForGroup(group)
  moduleFamilies = moduleFamilies.filter((f) => allowedTiers.has(f.tier))
  return moduleFamilies
    .map((def) => {
      const key = getFamilyMasteryKey(def.familyId, group)
      return familyMastery[key]
    })
    .filter(
      (state): state is FamilyMasteryState =>
        state !== undefined && state.masteryScore >= 85,
    )
}

/**
 * Get weakest families for a module (for "Continue Learning" recommendations).
 * Returns families sorted by mastery score (lowest first), excluding unseen.
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param moduleId - Module to query
 * @param group - School group (REQUIRED)
 * @param limit - Maximum number of families to return (default: 3)
 * @returns Array of weakest family states
 */
export function getWeakestFamiliesForModule(
  familyMastery: Record<string, FamilyMasteryState>,
  moduleId: ModuleId,
  group: GroupId,
  limit: number = 3,
): FamilyMasteryState[] {
  let moduleFamilies = getFamiliesForModule(moduleId)

  // Filter by group tier range
  const allowedTiers = getAllowedTiersForGroup(group)
  moduleFamilies = moduleFamilies.filter((f) => allowedTiers.has(f.tier))
  return moduleFamilies
    .map((def) => {
      const key = getFamilyMasteryKey(def.familyId, group)
      return familyMastery[key]
    })
    .filter(
      (state): state is FamilyMasteryState =>
        state !== undefined && state.attempts > 0,
    )
    .sort((a, b) => a.masteryScore - b.masteryScore)
    .slice(0, limit)
}

/**
 * Get weakest families across ALL modules (for cross-module session recommendations).
 * Returns families sorted by mastery score (lowest first), excluding unseen.
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param group - School group (REQUIRED)
 * @param limit - Maximum number of families to return (default: 3)
 * @returns Array of weakest family states across all modules
 */
export function getWeakestFamiliesGlobal(
  familyMastery: Record<string, FamilyMasteryState>,
  group: GroupId,
  limit: number = 3,
): FamilyMasteryState[] {
  const allowedTiers = getAllowedTiersForGroup(group)
  const groupSuffix = `_g${group}`

  return Object.entries(familyMastery)
    .filter(([key, state]) => {
      if (!key.endsWith(groupSuffix)) return false
      if (!state || state.attempts === 0) return false
      // Extract familyId and check tier is allowed for this group
      const familyId = key.slice(0, -groupSuffix.length)
      const def = getFamilyDefinition(familyId)
      return def ? allowedTiers.has(def.tier) : false
    })
    .map(([_, state]) => state)
    .sort((a, b) => a.masteryScore - b.masteryScore)
    .slice(0, limit)
}

/**
 * Get count of families by mastery level for a module.
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param moduleId - Module to query
 * @param group - School group (REQUIRED)
 * @returns Counts by mastery level
 */
export function countFamiliesByMasteryLevel(
  familyMastery: Record<string, FamilyMasteryState>,
  moduleId: ModuleId,
  group: GroupId,
): {
  unseen: number
  beginner: number // < BEGINNER threshold
  oefenaar: number // BEGINNER..OEFENAAR
  gevorderd: number // OEFENAAR..GEVORDERD
  meester: number // >= GEVORDERD threshold
} {
  let moduleFamilies = getFamiliesForModule(moduleId)

  // Filter by group tier range
  const allowedTiers = getAllowedTiersForGroup(group)
  moduleFamilies = moduleFamilies.filter((f) => allowedTiers.has(f.tier))
  const counts = {
    unseen: 0,
    beginner: 0,
    oefenaar: 0,
    gevorderd: 0,
    meester: 0,
  }
  moduleFamilies.forEach((def) => {
    const key = getFamilyMasteryKey(def.familyId, group)
    const state = familyMastery[key]
    if (!state || state.attempts === 0) {
      counts.unseen++
    } else if (state.masteryScore < MASTERY_LEVEL_THRESHOLDS.BEGINNER) {
      counts.beginner++
    } else if (state.masteryScore < MASTERY_LEVEL_THRESHOLDS.OEFENAAR) {
      counts.oefenaar++
    } else if (state.masteryScore < MASTERY_LEVEL_THRESHOLDS.GEVORDERD) {
      counts.gevorderd++
    } else {
      counts.meester++
    }
  })
  return counts
}
